import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ERAS = [
  {
    n: "I",
    title: "Trade & Trust",
    range: "Pre-1800s",
    body:
      "Handshakes, ledgers and word-of-mouth. Business was built on relationship, reputation and personal accountability.",
  },
  {
    n: "II",
    title: "Paper & Process",
    range: "1800s — 1950s",
    body:
      "Invoices, forms and filing cabinets. Process and documentation made trade scalable — but tied businesses to admin.",
  },
  {
    n: "III",
    title: "Computers & Records",
    range: "1960s — 1990s",
    body:
      "Spreadsheets, accounting software and structured data brought speed — and a new layer of complexity to manage.",
  },
  {
    n: "IV",
    title: "Websites & Reach",
    range: "1995 — 2010",
    body:
      "Brochures became websites. Marketing moved online. Visibility grew, but so did fragmentation across channels.",
  },
  {
    n: "V",
    title: "Automation & Workflow",
    range: "2010 — 2022",
    body:
      "SaaS, CRMs and integrations promised connected work. Most businesses ended up with stacks of disconnected tools.",
  },
  {
    n: "VI",
    title: "Practical AI",
    range: "2023 →",
    body:
      "Voice capture, structured prompts and AI-supported admin reduce friction. The tools change again — the principles don't.",
  },
];

export default function Timeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 30%"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      ref={ref}
      data-testid="timeline-section"
      className="relative py-20 md:py-28 bg-bg-muted border-y border-ink/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 md:mb-16">
          <div className="lg:col-span-4">
            <div className="overline">Business through the ages</div>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              The tools<br />
              keep changing.<br />
              <em className="font-light text-ink-soft">The need for structure</em>
              <br /> doesn't.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 self-end">
            <p className="text-base md:text-lg leading-relaxed text-ink/80 max-w-xl">
              Each era promised to solve the last era's friction. The work
              still needs to be captured, organised, communicated and moved
              forward. ETradingGroup helps you bring the pieces together —
              whatever stage you're operating in.
            </p>
          </div>
        </div>

        <div className="relative grid grid-cols-[40px_1fr] md:grid-cols-[64px_1fr] gap-8 md:gap-12">
          {/* Sticky timeline rail */}
          <div className="relative">
            <div className="sticky top-32 h-[60vh]">
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-ink/10" />
              <motion.div
                style={{ height: lineHeight }}
                className="absolute left-1/2 -translate-x-1/2 top-0 w-px bg-brand-red origin-top"
              />
            </div>
          </div>

          <ol className="space-y-16 md:space-y-20">
            {ERAS.map((era, i) => (
              <motion.li
                key={era.n}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`timeline-era-${i + 1}`}
                className="relative"
              >
                {/* dot */}
                <span className="absolute -left-[40px] md:-left-[64px] top-2 w-px h-px">
                  <span className="absolute -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 w-3 h-3 rounded-full bg-bg-base border border-ink/30" />
                  <motion.span
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ delay: 0.15, duration: 0.5 }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 w-2 h-2 rounded-full bg-brand-red"
                  />
                </span>

                <div className="flex items-baseline gap-6 mb-3">
                  <span className="overline text-brand-red">{era.n}</span>
                  <span className="overline text-ink-soft">{era.range}</span>
                  {/* Tiny per-era connector line */}
                  <span className="hidden md:inline-block flex-1 h-px bg-ink/10 ml-3" />
                </div>
                <h3 className="font-serif-display text-3xl md:text-5xl tracking-tight text-ink">
                  {era.title}
                </h3>
                <p className="mt-4 text-[16px] md:text-lg leading-relaxed text-ink/75 max-w-xl">
                  {era.body}
                </p>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
